import React from 'react';
import CMSCustomSections from '../components/CMSCustomSections';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Building2, 
  Users, 
  Car, 
  Clock, 
  Shield, 
  FileText,
  CheckCircle,
  ArrowRight,
  Briefcase,
  Plane,
  Calendar,
  CreditCard
} from 'lucide-react';
import { useCMS, getImageUrl } from '../hooks/useCMS';

const Corporate = () => {
  const navigate = useNavigate();
  const { content } = useCMS('corporate');

  const corporateServices = [
    {
      icon: Briefcase,
      title: 'Executive Transportation',
      description: 'Premium sedan and SUV service for executives, board members, and VIP guests',
      features: ['Dedicated chauffeurs', 'Flight tracking', 'Meet & greet service', 'Confidential travel']
    },
    {
      icon: Plane,
      title: 'Airport Transfers',
      description: 'Reliable ground transportation for business travelers across all Atlanta airports',
      features: ['Real-time tracking', 'Flight delay monitoring', 'Curbside pickup', 'Corporate billing']
    },
    {
      icon: Users,
      title: 'Group Transportation',
      description: 'Coordinated transport for conferences, team events, and client entertainment',
      features: ['Multi-vehicle dispatch', 'Event coordination', 'Custom itineraries', 'On-site management']
    },
    {
      icon: Car,
      title: 'Fleet Programs',
      description: 'Comprehensive vehicle management for company-owned or leased vehicles',
      features: ['Maintenance coordination', 'Driver services', 'Vehicle sourcing', 'Disposition assistance']
    }
  ];

  const benefits = [
    {
      icon: CreditCard,
      title: 'Simplified Billing',
      description: 'Consolidated monthly invoicing with detailed trip reporting and expense categorization'
    },
    {
      icon: Clock,
      title: 'Priority Booking',
      description: '24/7 booking with priority dispatch and dedicated account management'
    },
    {
      icon: Shield,
      title: 'Liability Coverage',
      description: 'Comprehensive insurance and vetted professional chauffeurs for peace of mind'
    },
    {
      icon: FileText,
      title: 'Compliance Ready',
      description: 'Documentation and reporting to meet corporate travel policy requirements'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="relative h-[500px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={getImageUrl(content('hero.background_image'), 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBvZmZpY2V8ZW58MHx8fHwxNzYxNjEzMDE2fDA&ixlib=rb-4.1.0&q=85')}
            alt="Corporate Services"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-transparent" />
        </div>
        
        <div className="relative h-full max-w-7xl mx-auto px-6 flex items-center">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="h-6 w-6 text-red-600" />
              <span className="text-red-600 font-semibold">CORPORATE SOLUTIONS</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {content('hero.title', 'Enterprise-Grade Auto Concierge').split(' ').slice(0, 2).join(' ')}<br />{content('hero.title', 'Enterprise-Grade Auto Concierge').split(' ').slice(2).join(' ')}
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              {content('hero.subtitle', 'Streamlined ground transportation and fleet management for businesses of all sizes')}
            </p>
            <div className="flex gap-4">
              <Button 
                onClick={() => navigate('/consultation')}
                className="h-14 px-8 bg-red-600 hover:bg-red-700 text-white text-lg font-semibold"
              >
                {content('hero.cta_primary', 'Schedule Demo')}
              </Button>
              <Button 
                onClick={() => navigate('/contact')}
                variant="outline"
                className="h-14 px-8 border-white text-white hover:bg-white hover:text-black text-lg font-semibold"
              >
                {content('hero.cta_secondary', 'Contact Sales')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Corporate Services */}
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-black mb-4">Corporate Transportation Solutions</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From executive travel to fleet management, ACE provides comprehensive mobility solutions tailored to your organization's needs
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {corporateServices.map((service, idx) => {
            const Icon = service.icon;
            return (
              <Card key={idx} className="p-8 border-gray-200 hover:shadow-xl transition-all">
                <div className="flex items-start gap-6">
                  <div className="h-16 w-16 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-8 w-8 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-black mb-2">{service.title}</h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <ul className="grid grid-cols-2 gap-2">
                      {service.features.map((feature, i) => (
                        <li key={i} className="flex items-center text-sm text-gray-600">
                          <CheckCircle className="h-4 w-4 text-red-600 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Benefits */}
      <div className="bg-gray-50 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-black text-center mb-16">Why Companies Choose ACE</h2>
          
          <div className="grid md:grid-cols-4 gap-8">
            {benefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <Card key={idx} className="p-6 text-center border-gray-200">
                  <div className="h-14 w-14 mx-auto rounded-full bg-red-600 flex items-center justify-center mb-4">
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-black mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Account Tiers */}
      <div className="max-w-7xl mx-auto px-6 py-24">
        <h2 className="text-4xl font-bold text-black text-center mb-4">Corporate Account Tiers</h2>
        <p className="text-xl text-gray-600 text-center mb-16">Flexible programs designed for your usage level</p>

        <div className="grid md:grid-cols-3 gap-8">
          <Card className="p-8 border-gray-200">
            <h3 className="text-2xl font-bold text-black mb-2">Starter</h3>
            <p className="text-gray-600 mb-6">For occasional business travel needs</p>
            <ul className="space-y-3 text-gray-600 mb-8">
              <li className="flex items-center">
                <CheckCircle className="h-5 w-5 text-gray-400 mr-3" />
                Pay-per-ride pricing
              </li>
              <li className="flex items-center">
                <CheckCircle className="h-5 w-5 text-gray-400 mr-3" />
                Online booking portal
              </li>
              <li className="flex items-center">
                <CheckCircle className="h-5 w-5 text-gray-400 mr-3" />
                Monthly invoicing
              </li>
              <li className="flex items-center">
                <CheckCircle className="h-5 w-5 text-gray-400 mr-3" />
                Basic reporting
              </li>
            </ul>
            <Button 
              onClick={() => navigate('/contact')}
              className="w-full bg-black hover:bg-gray-900 text-white"
            >
              Get Started
            </Button>
          </Card>

          <Card className="p-8 border-2 border-red-600 relative">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-red-600 text-white px-6 py-1 rounded-full text-sm font-semibold">
              MOST POPULAR
            </div>
            <h3 className="text-2xl font-bold text-black mb-2">Business</h3>
            <p className="text-gray-600 mb-6">For regular corporate transportation</p>
            <ul className="space-y-3 text-gray-600 mb-8">
              <li className="flex items-center">
                <CheckCircle className="h-5 w-5 text-red-600 mr-3" />
                15% volume discount
              </li>
              <li className="flex items-center">
                <CheckCircle className="h-5 w-5 text-red-600 mr-3" />
                Priority dispatch
              </li>
              <li className="flex items-center">
                <CheckCircle className="h-5 w-5 text-red-600 mr-3" />
                Dedicated account manager
              </li>
              <li className="flex items-center">
                <CheckCircle className="h-5 w-5 text-red-600 mr-3" />
                Advanced analytics
              </li>
              <li className="flex items-center">
                <CheckCircle className="h-5 w-5 text-red-600 mr-3" />
                Multiple user accounts
              </li>
            </ul>
            <Button 
              onClick={() => navigate('/consultation')}
              className="w-full bg-red-600 hover:bg-red-700 text-white"
            >
              Schedule Demo
            </Button>
          </Card>

          <Card className="p-8 border-gray-200">
            <h3 className="text-2xl font-bold text-black mb-2">Enterprise</h3>
            <p className="text-gray-600 mb-6">For large organizations with complex needs</p>
            <ul className="space-y-3 text-gray-600 mb-8">
              <li className="flex items-center">
                <CheckCircle className="h-5 w-5 text-gray-400 mr-3" />
                Custom pricing
              </li>
              <li className="flex items-center">
                <CheckCircle className="h-5 w-5 text-gray-400 mr-3" />
                API integration
              </li>
              <li className="flex items-center">
                <CheckCircle className="h-5 w-5 text-gray-400 mr-3" />
                Dedicated fleet option
              </li>
              <li className="flex items-center">
                <CheckCircle className="h-5 w-5 text-gray-400 mr-3" />
                Custom reporting
              </li>
              <li className="flex items-center">
                <CheckCircle className="h-5 w-5 text-gray-400 mr-3" />
                SLA guarantees
              </li>
            </ul>
            <Button 
              onClick={() => navigate('/contact')}
              className="w-full bg-black hover:bg-gray-900 text-white"
            >
              Contact Sales
            </Button>
          </Card>
        </div>
      </div>

      {/* Use Cases */}
      <div className="bg-gray-50 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-black text-center mb-16">Corporate Use Cases</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-8 border-gray-200">
              <Calendar className="h-10 w-10 text-red-600 mb-4" />
              <h3 className="text-xl font-bold text-black mb-2">Conferences & Events</h3>
              <p className="text-gray-600">
                Coordinate ground transportation for attendees, speakers, and VIPs at your corporate events. We handle logistics so you can focus on the content.
              </p>
            </Card>

            <Card className="p-8 border-gray-200">
              <Plane className="h-10 w-10 text-red-600 mb-4" />
              <h3 className="text-xl font-bold text-black mb-2">Executive Travel</h3>
              <p className="text-gray-600">
                Reliable, discreet transportation for C-suite executives and board members. Professional chauffeurs trained in confidentiality and security protocols.
              </p>
            </Card>

            <Card className="p-8 border-gray-200">
              <Users className="h-10 w-10 text-red-600 mb-4" />
              <h3 className="text-xl font-bold text-black mb-2">Client Entertainment</h3>
              <p className="text-gray-600">
                Impress clients and prospects with premium transportation. From airport pickup to dinner reservations, we ensure a seamless experience.
              </p>
            </Card>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-black text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Streamline Your Corporate Transportation?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Schedule a demo to see how ACE can simplify mobility for your organization
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/consultation')}
              size="lg"
              className="h-14 px-8 bg-red-600 hover:bg-red-700 text-white text-lg"
            >
              Schedule Demo
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              onClick={() => navigate('/contact')}
              size="lg"
              variant="outline"
              className="h-14 px-8 border-white text-white hover:bg-white hover:text-black text-lg"
            >
              Contact Sales
            </Button>
          </div>
        </div>
      </div>

      {/* Referral Section */}
      <div className="max-w-7xl mx-auto px-6 py-24" data-testid="corporate-referral">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-black mb-4">Partner Referral Program</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Know someone who could benefit from ACE services? Refer them and both parties earn rewards through our loyalty program.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          <Card className="p-6 border-gray-200 text-center">
            <div className="h-14 w-14 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
              <span className="text-2xl font-bold text-red-600">1</span>
            </div>
            <h3 className="font-bold text-black mb-2">Share Your Code</h3>
            <p className="text-sm text-gray-600">Every ACE member gets a unique referral code in their Client Portal</p>
          </Card>
          <Card className="p-6 border-gray-200 text-center">
            <div className="h-14 w-14 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
              <span className="text-2xl font-bold text-red-600">2</span>
            </div>
            <h3 className="font-bold text-black mb-2">Friend Books</h3>
            <p className="text-sm text-gray-600">Your referral books their first ACE experience</p>
          </Card>
          <Card className="p-6 border-gray-200 text-center">
            <div className="h-14 w-14 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
              <span className="text-2xl font-bold text-red-600">3</span>
            </div>
            <h3 className="font-bold text-black mb-2">Both Earn</h3>
            <p className="text-sm text-gray-600">You get 500 points + 1 free hour. They get 250 points.</p>
          </Card>
        </div>
        <div className="text-center mt-8">
          <Button onClick={() => navigate('/portal/rewards')} className="bg-red-600 hover:bg-red-700 text-white h-12 px-8 text-base">
            Access Your Referral Code <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
      <CMSCustomSections pageId="corporate" dark />
    </div>
  );
};

export default Corporate;
